import { isServer } from '@/helpers';
import type { SearchType } from '@/hooks/search/useSearch.helpers';
import { useSyncExternalStore } from 'react';

const SMT_ONLY_TOGGLE_KEY = 'deals_smt_only_toggle';
const SMT_ONLY_TOGGLE_EVENT = 'deals_smt_only_toggle_change';

function getUserChoice(): boolean | undefined {
  const choice = sessionStorage.getItem(SMT_ONLY_TOGGLE_KEY);
  return choice === null ? undefined : choice === '1';
}

export function isSmtPage(searchType?: SearchType): boolean {
  return searchType === 'deals_sistaminuten';
}

export function isRedirectedFromPreview(state: unknown): boolean {
  return (state as { redirectedFromPreview?: unknown } | null | undefined)?.redirectedFromPreview === true;
}

// A redirected SMT SERP is what the preview shows for this search, so either toggle position matches it.
export function isSmtModeInSync(smtOnly: boolean, searchType?: SearchType, redirectedFromPreview = false): boolean {
  return isSmtPage(searchType) ? smtOnly || redirectedFromPreview : !smtOnly;
}

function notify() {
  window.dispatchEvent(new Event(SMT_ONLY_TOGGLE_EVENT));
}

function subscribe(onChange: () => void) {
  window.addEventListener(SMT_ONLY_TOGGLE_EVENT, onChange);
  return () => window.removeEventListener(SMT_ONLY_TOGGLE_EVENT, onChange);
}

export function isSmtOnlySearchEnabled(searchType?: SearchType, redirectedFromPreview = false): boolean {
  if (isServer) return isSmtPage(searchType);
  return getUserChoice() ?? (isSmtPage(searchType) && !redirectedFromPreview);
}

function setSmtOnlySearchToggle(value: boolean) {
  sessionStorage.setItem(SMT_ONLY_TOGGLE_KEY, value ? '1' : '0');
  notify();
}

export function clearSmtOnlySearchToggle() {
  sessionStorage.removeItem(SMT_ONLY_TOGGLE_KEY);
  notify();
}

export function useSmtOnlyToggle(searchType?: SearchType, redirectedFromPreview = false) {
  const isSmtOnlySearch = useSyncExternalStore(
    subscribe,
    () => isSmtOnlySearchEnabled(searchType, redirectedFromPreview),
    () => isSmtPage(searchType),
  );

  function setIsSmtOnlySearch(value: boolean | ((prev: boolean) => boolean)) {
    setSmtOnlySearchToggle(
      typeof value === 'function' ? value(isSmtOnlySearchEnabled(searchType, redirectedFromPreview)) : value,
    );
  }

  return { isSmtOnlySearch, setIsSmtOnlySearch };
}
